import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { lazy, Suspense, type ComponentType } from 'react';
import PageMetadata from '@/components/PageMetadata';
import RouteSuspenseFallback from '@/components/RouteSuspenseFallback';
import { DEV_BUILD } from '@/config/devEnv';

// Import layouts (not lazy – always needed for shell)
import PublicLayout from '@/layouts/PublicLayout';
import AuthLayout from '@/layouts/AuthLayout';
import AuthenticatedLayout from '@/layouts/AuthenticatedLayout';
import NotFoundLayout from '@/features/errors/NotFoundLayout';

// Eagerly loaded – critical path pages
import HomePage from '@/features/games/pages/HomePage';
import UserLoginPage from '@/features/auth/pages/UserLoginPage';

/** Lazy import + reload when a chunk fails (new deploy, stale cache) */
function lazyReload(factory: () => Promise<{ default: ComponentType<any> }>) {
  return lazy(() =>
    factory().catch(() => {
      window.location.reload();
      return new Promise(() => {}) as Promise<{ default: ComponentType<any> }>;
    })
  );
}

// Lazy-loaded pages – code-split for smaller initial bundle
const SettingsPage = lazyReload(() => import('@/features/account/pages/SettingsPage'));
const AnimationSettingsPage = lazyReload(() => import('@/features/account/pages/AnimationSettingsPage'));
const AccountSecurityPage = lazyReload(() => import('@/features/account/pages/AccountSecurityPage'));
const MessagesPage = lazyReload(() => import('@/features/account/pages/MessagesPage'));
const GamesPage = lazyReload(() => import('@/features/games/pages/GamesPage'));
/** Full-screen in-app game player (non-kiosk only; kiosks redirect inside the page). */
const GamePlayerPage = lazyReload(() => import('@/features/games/pages/GamePlayerPage'));
const DepositPage = lazyReload(() => import('@/features/finance/pages/DepositPage'));
const PaymentCheckoutPage = lazyReload(() => import('@/features/finance/pages/PaymentCheckoutPage'));
const WithdrawalLimitsLayout = lazyReload(() => import('@/features/finance/layouts/WithdrawalLimitsLayout'));
const WalletPage = lazyReload(() => import('@/features/finance/pages/WalletPage'));
const WalletTopUpPage = lazyReload(() => import('@/features/finance/pages/WalletTopUpPage'));
const WalletDepositCheckoutPage = lazyReload(() => import('@/features/finance/pages/WalletDepositCheckoutPage'));
const WalletPayoutRequestPage = lazyReload(() => import('@/features/finance/pages/WalletPayoutRequestPage'));
const TransactionsPage = lazyReload(() => import('@/features/finance/pages/TransactionsPage'));
const ActivatePage = lazyReload(() => import('@/features/auth/pages/ActivatePage'));
const ResetPasswordPage = lazyReload(() => import('@/features/auth/pages/ResetPasswordPage'));
const SupportPage = lazyReload(() => import('@/features/support/pages/SupportPage'));
const NotFoundPage = lazyReload(() => import('@/features/errors/NotFoundPage'));
// The four tabs' hubs and the guided money flows
const MoneyPage = lazyReload(() => import('@/features/money/pages/MoneyPage'));
const AddMoneyPage = lazyReload(() => import('@/features/money/pages/AddMoneyPage'));
const CashOutPage = lazyReload(() => import('@/features/money/pages/CashOutPage'));
const HelpPage = lazyReload(() => import('@/features/help/pages/HelpPage'));
const MePage = lazyReload(() => import('@/features/me/pages/MePage'));
/** Dev-only component catalogue (/dev/ui); compiled out of production builds. */
const UiCatalogPage = DEV_BUILD ? lazyReload(() => import('@/dev/UiCatalogPage')) : null;

/**
 * An old URL → its page's new home (spec §3). Keeps the query (`?tab=`, an email ticket link's
 * `?t=`), the hash and the navigation state (e.g. the wallet's success banners); `search` adds
 * to the query (`/faq` → `?section=faq`). `:name` in `to` takes the matched route param.
 */
function MovedTo({ to, search }: { to: string; search?: Record<string, string> }) {
  const location = useLocation();
  const params = useParams();
  const pathname = to.replace(/:(\w+)/g, (_match, name: string) => encodeURIComponent(params[name] ?? ''));
  let query = location.search;
  if (search) {
    const merged = new URLSearchParams(location.search);
    Object.entries(search).forEach(([key, value]) => merged.set(key, value));
    query = `?${merged.toString()}`;
  }
  return <Navigate replace to={{ pathname, search: query, hash: location.hash }} state={location.state} />;
}

function AppRouter() {
  return (
    <>
      <PageMetadata
        title="Kiosk Gaming"
        description="Professional Gaming Kiosk Platform"
      />
      <Suspense fallback={<RouteSuspenseFallback />}>
        <Routes>
          {/* Public Routes */}
          <Route element={<PublicLayout />}>
            {/* Play */}
            <Route path="/" element={<HomePage />} />
            {/* Help (open to guests) and the ticket conversation, e.g. from a reply email */}
            <Route path="/help" element={<HelpPage />} />
            <Route path="/help/tickets/:ticketNumber" element={<SupportPage />} />
          </Route>

          {UiCatalogPage ? (
            <Route element={<PublicLayout />}>
              <Route path="/dev/ui" element={<UiCatalogPage />} />
            </Route>
          ) : null}

          {/* Auth Routes */}
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<UserLoginPage />} />
            <Route path="/activate" element={<ActivatePage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
          </Route>

          {/* Old URLs (bookmarks, emails, links in the app) → the four tabs */}
          <Route path="/account" element={<MovedTo to="/me/details" />} />
          <Route path="/account/dashboard" element={<MovedTo to="/me/details" />} />
          <Route path="/account/settings" element={<MovedTo to="/me/details" />} />
          <Route path="/account/email-phone" element={<MovedTo to="/me/details" />} />
          <Route path="/account/security" element={<MovedTo to="/me/security" />} />
          <Route path="/account/change-password" element={<MovedTo to="/me/security" />} />
          <Route path="/account/animations" element={<MovedTo to="/me/sound-motion" />} />
          <Route path="/account/messages" element={<MovedTo to="/me/messages" />} />
          <Route path="/account/games" element={<MovedTo to="/money/games" />} />
          {/* Legacy game-withdraw flow → My games (Withdraw opens GameWithdrawModal) */}
          <Route path="/account/finance/withdrawals" element={<MovedTo to="/money/games" />} />
          <Route path="/account/finance/payout-request" element={<MovedTo to="/money/games" />} />
          <Route path="/account/finance/wallet" element={<MovedTo to="/money/wallet" />} />
          <Route path="/account/transactions" element={<MovedTo to="/money/history" />} />
          <Route path="/account/support/tickets/:ticketNumber" element={<MovedTo to="/help/tickets/:ticketNumber" />} />
          <Route path="/support" element={<MovedTo to="/help" />} />
          <Route path="/support/tickets/:ticketNumber" element={<MovedTo to="/help/tickets/:ticketNumber" />} />
          <Route path="/faq" element={<MovedTo to="/help" search={{ section: 'faq' }} />} />

          {/* Authenticated Routes - with Header and Footer */}
          <Route element={<AuthenticatedLayout />}>
            {/* Money */}
            <Route path="/money" element={<MoneyPage />} />
            <Route path="/money/add" element={<AddMoneyPage />} />
            <Route path="/money/history" element={<TransactionsPage />} />
            <Route path="/money/games" element={<GamesPage />} />
            {/* Play: a game allowed inside the app, full screen over the chrome (FullscreenOverlay) */}
            <Route path="/play/:gameId" element={<GamePlayerPage />} />
            {/* Me */}
            <Route path="/me" element={<MePage />} />
            <Route path="/me/details" element={<SettingsPage />} />
            <Route path="/me/security" element={<AccountSecurityPage />} />
            <Route path="/me/sound-motion" element={<AnimationSettingsPage />} />
            <Route path="/me/messages" element={<MessagesPage />} />
            {/* Detailed money tools: keep their URLs (resume links, gateway returns) */}
            <Route path="/account/finance/deposit" element={<DepositPage />} />
            <Route path="/account/finance/payment-checkout" element={<PaymentCheckoutPage />} />
            <Route element={<WithdrawalLimitsLayout />}>
              <Route path="/money/wallet" element={<WalletPage />} />
              <Route path="/money/cash-out" element={<CashOutPage />} />
              <Route path="/account/finance/wallet/deposit" element={<WalletTopUpPage />} />
              <Route path="/account/wallet/payout-request" element={<WalletPayoutRequestPage />} />
            </Route>
            <Route path="/account/wallet/deposit-checkout" element={<WalletDepositCheckoutPage />} />
            <Route
              path="/account/wallet/add-funds"
              element={<Navigate to="/account/finance/wallet/deposit" replace />}
            />
            <Route
              path="/account/finance/wallet/withdraw"
              element={<Navigate to="/account/wallet/payout-request" replace />}
            />
          </Route>

          {/* Unknown paths: a "Page not found" screen, keeping the URL as typed */}
          <Route element={<NotFoundLayout />}>
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Suspense>
    </>
  );
}

export default AppRouter;
